import {useDispatch, useSelector} from 'react-redux';
import { useNavigate } from 'react-router-dom';
import React, { useState } from "react";
import {UserMenu} from "./UserMenu";
import {Layout, Space, Button, Drawer, Modal, Form, Input, message} from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import {exitSpace, getCurrentSpaceThunk, createSpaceThunk, getSpacesThunk} from "../Feature/SpacesSlice";
import { MenuOutlined, DownOutlined } from '@ant-design/icons';
import "./Navbar.css"
import {useSpacePersistence} from "../hooks/useSpacePersistence";

export const Navbar = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showCreateSpace, setShowCreateSpace] = useState(false);
    const [spaceSelectorOpen, setSpaceSelectorOpen] = useState(false);
    const [form] = Form.useForm();
    const { saveLastSpaceId, clearLastSpaceId } = useSpacePersistence();

    const user = useSelector((state) => {
        return state.firebase.auth
    });

    const currentSpace = useSelector((state) => {
        return state.spaces.current
    });

    const spaces = useSelector((state) => {
        return state.spaces.spaces
    });

    const openLogin = () => {
        navigate('/login')
        setMobileMenuOpen(false);
    };

    const exitSpaceAction = () => {
        clearLastSpaceId();
        dispatch(exitSpace());
        setMobileMenuOpen(false);
        navigate('/spaces');
    };

    const switchSpace = (spaceId) => {
        dispatch(getCurrentSpaceThunk(spaceId)).then(() => {
            saveLastSpaceId(spaceId);
            navigate(`/spaces/${spaceId}/`);
            setMobileMenuOpen(false);
        });
    };

    const handleCreateSpace = () => {
        form
            .validateFields()
            .then((values) => {
                form.resetFields();
                setShowCreateSpace(false);
                dispatch(createSpaceThunk(values))
                    .then(() => dispatch(getSpacesThunk()))
                    .then(() => message.success(`Created space ${values.spaceName}`));
            });
    };

    const getSpaceInitial = () => {
        return currentSpace.data?.space_name?.charAt(0).toUpperCase() || 'A';
    };

    const headerContent = currentSpace.data ? (
        <>
            <div
                onClick={() => setSpaceSelectorOpen(!spaceSelectorOpen)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    userSelect: 'none'
                }}
            >
                <div
                    style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#7c3aed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '18px',
                        flexShrink: 0
                    }}
                >
                    {getSpaceInitial()}
                </div>
                <div style={{flex: 1, overflow: 'hidden'}}>
                    <div style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(0, 0, 0, 0.85)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {currentSpace.data.space_name}
                    </div>
                </div>
                <DownOutlined style={{
                    fontSize: '12px',
                    flexShrink: 0,
                    transition: 'transform 0.3s',
                    transform: spaceSelectorOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }} />
            </div>

            {spaceSelectorOpen && (
                <div style={{
                    backgroundColor: '#fafafa',
                    borderTop: '1px solid #f0f0f0',
                    borderBottom: '1px solid #f0f0f0',
                    padding: '8px 0'
                }}>
                    {spaces && spaces.length > 0 && spaces.map(space => (
                        <Button
                            key={space.id}
                            type="text"
                            block
                            onClick={() => {
                                switchSpace(space.id);
                                setSpaceSelectorOpen(false);
                            }}
                            style={{
                                textAlign: 'left',
                                paddingLeft: '24px',
                                paddingRight: '16px',
                                height: '36px',
                                fontWeight: space.id === currentSpace.data?.id ? '600' : '400',
                                color: space.id === currentSpace.data?.id ? '#7c3aed' : 'rgba(0, 0, 0, 0.85)'
                            }}
                        >
                            {space.space_name}
                        </Button>
                    ))}
                    <div style={{borderTop: '1px solid #f0f0f0', margin: '8px 0'}} />
                    <Button
                        type="text"
                        block
                        onClick={() => {
                            exitSpaceAction();
                            setSpaceSelectorOpen(false);
                        }}
                        style={{
                            textAlign: 'left',
                            paddingLeft: '24px',
                            paddingRight: '16px',
                            height: '36px',
                            color: 'rgba(0, 0, 0, 0.65)'
                        }}
                    >
                        Exit Space
                    </Button>
                </div>
            )}
        </>
    ) : (
        <PageHeader
            className="site-page-header"
            avatar={{src: process.env.PUBLIC_URL + '/avocado_logo.png'}}
            title={"Avocados"}
        />
    );

    const menuContent = (
        <Space direction="vertical" style={{width: '100%'}}>
            {headerContent}

            {user.isEmpty ? <Button type="text" onClick={openLogin}>Login</Button> :
                <UserMenu/>}

            <Modal
                title="Create Space"
                open={showCreateSpace}
                okText="Create Space"
                onOk={handleCreateSpace}
                onCancel={() => {
                    form.resetFields();
                    setShowCreateSpace(false);
                }}
            >
                <Form
                    form={form}
                    name="create-space-form"
                    labelCol={{span: 8}}
                    wrapperCol={{span: 16}}
                    autoComplete="off"
                >
                    <Form.Item
                        label="Space Name"
                        name="spaceName"
                        rules={[{required: true, message: 'Please enter a space name!'}]}
                    >
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );

    return (
        <>
            {/* Desktop Sidebar */}
            <Layout.Sider
                breakpoint="md"
                collapsedWidth="0"
                className="desktop-sidebar"
            >
                {menuContent}
            </Layout.Sider>

            {/* Mobile Hamburger Button */}
            <div className="mobile-header">
                <Button
                    type="text"
                    icon={<MenuOutlined />}
                    onClick={() => setMobileMenuOpen(true)}
                    className="hamburger-button"
                />
            </div>

            {/* Mobile Menu Overlay */}
            <Drawer
                title="Menu"
                placement="left"
                onClose={() => setMobileMenuOpen(false)}
                open={mobileMenuOpen}
                bodyStyle={{ padding: 0 }}
                headerStyle={{ display: 'none' }}
                width="65%"
                className="mobile-menu-drawer"
            >
                {menuContent}
            </Drawer>
        </>
    )
};
